import { useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { TrendDelta } from "../../../components/TrendDelta";
import { FilterSwitchGroup } from "../../../components/FilterSwitchGroup";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";
import { brand } from "../../../theme/theme";

// Which field of a timeseries point each toggle reads, and how to format it.
const METRICS = [
  { key: "sessions", label: "Sesiones", format: (v) => Math.round(v).toLocaleString("es-ES") },
  { key: "total_users", label: "Usuarios", format: (v) => Math.round(v).toLocaleString("es-ES") },
  { key: "key_events", label: "Eventos clave", format: (v) => Math.round(v).toLocaleString("es-ES") },
  { key: "conversion_rate", label: "Conversión", format: (v) => `${v.toFixed(2)}%` },
];

// The chart's metric tabs use the app's standard tab bar, the same one as the
// "Origen del tráfico" table. On narrow screens the strip scrolls sideways.
const METRIC_TAB_OPTIONS = METRICS.map((option) => ({
  value: option.key,
  label: option.label,
}));
const NO_WRAP_SX = { flexWrap: "nowrap" };

/**
 * The plot's own box, published so a caller can line something else up with it
 * rather than with the whole component: the metric tabs (32px) and the gap
 * above the plot are what separate one from the other.
 */
export const TRAFFIC_CHART_PLOT_HEIGHT = 280;
export const TRAFFIC_CHART_PLOT_OFFSET = 48;

function MetricToggle({ metric, onChange }) {
  return (
    <Box
      sx={{
        overflowX: "auto",
        maxWidth: "100%",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <FilterSwitchGroup
        options={METRIC_TAB_OPTIONS}
        selectedValue={metric}
        onChange={onChange}
        containerSx={NO_WRAP_SX}
        optionsSx={NO_WRAP_SX}
      />
    </Box>
  );
}

function formatDayLabel(isoDate) {
  try {
    return format(parseISO(isoDate), "d MMM", { locale: es });
  } catch {
    return isoDate;
  }
}

function formatFullDayLabel(isoDate) {
  try {
    const label = format(parseISO(isoDate), "EEEE d 'de' MMMM", { locale: es });
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return isoDate;
  }
}

/** Percent change vs. the same point in the previous period, current − previous. */
function changePercent(current, previous) {
  if (previous === undefined || previous === null || previous === 0) return null;
  if (current === undefined || current === null) return null;
  return ((current - previous) / previous) * 100;
}

/** Percent change next to a tooltip row. */
function TooltipDelta({ percent }) {
  return <TrendDelta value={percent} suffix="%" maximumFractionDigits={1} size="small" />;
}

function ChartTooltipContent({
  active,
  payload,
  neutrals,
  compareEnabled,
  metric,
  activeMetric,
  showKeyEventsBar,
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <Box
      sx={{
        backgroundColor: "white",
        border: `1px solid ${neutrals.gray[200]}`,
        borderRadius: "8px",
        padding: 1.5,
        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, display: "block", marginBottom: 0.5 }}>
        {formatFullDayLabel(point.date)}
      </Typography>
      <Stack spacing={0.25}>
        {/* Whatever the toggle has selected is what the line draws, so it leads the tooltip too. */}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {activeMetric.label}: <strong>{activeMetric.format(point[metric] ?? 0)}</strong>
          </Typography>
          {compareEnabled && (
            <TooltipDelta percent={changePercent(point[metric], point.previous_value)} />
          )}
        </Stack>
        {/* The grey bar is always key events, shown as context unless it's already the selected line. */}
        {showKeyEventsBar && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Eventos clave: <strong>{Math.round(point.key_events).toLocaleString("es-ES")}</strong>
            </Typography>
            {compareEnabled && (
              <TooltipDelta percent={changePercent(point.key_events, point.previous_key_events)} />
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

/**
 * Daily traffic chart: a line for the selected metric, key events as bars for
 * context, and a dashed line for the previous period when comparison is on.
 */
export function TrafficChart({ timeseries, compareEnabled }) {
  const theme = useTheme();
  const { neutrals } = useCustomTheme();
  const [metric, setMetric] = useState("sessions");

  const activeMetric = METRICS.find((m) => m.key === metric);
  const showKeyEventsBar = metric !== "key_events";

  const data = useMemo(() => {
    const current = timeseries?.current || [];
    const previous = timeseries?.previous || [];
    return current.map((point, index) => ({
      ...point,
      previous_value: compareEnabled ? previous[index]?.[metric] : undefined,
      previous_sessions: compareEnabled ? previous[index]?.sessions : undefined,
      previous_key_events: compareEnabled ? previous[index]?.key_events : undefined,
    }));
  }, [timeseries, metric, compareEnabled]);

  if (!data.length) return null;

  return (
    <Stack spacing={2} sx={{ height: "100%" }}>
      <MetricToggle metric={metric} onChange={setMetric} />

      {/* Grows with the card beside it; TRAFFIC_CHART_PLOT_HEIGHT is the floor. */}
      <Box sx={{ flex: 1, minHeight: TRAFFIC_CHART_PLOT_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={neutrals.gray[200]} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDayLabel}
              stroke={neutrals.gray[500]}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: neutrals.gray[200] }}
              minTickGap={24}
            />
            <YAxis hide domain={[0, "auto"]} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  neutrals={neutrals}
                  compareEnabled={compareEnabled}
                  metric={metric}
                  activeMetric={activeMetric}
                  showKeyEventsBar={showKeyEventsBar}
                />
              }
              cursor={{ stroke: neutrals.gray[300], strokeDasharray: "3 3" }}
            />

            {showKeyEventsBar && (
              <Bar
                dataKey="key_events"
                name="Eventos clave"
                fill={alpha(theme.palette.primary.main, 0.15)}
                radius={[3, 3, 0, 0]}
                barSize={10}
              />
            )}

            <Line
              type="monotone"
              dataKey={metric}
              name={activeMetric.label}
              stroke={theme.palette.primary.main}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 4 }}
            />

            {compareEnabled && (
              <Line
                type="monotone"
                dataKey="previous_value"
                name="Periodo anterior"
                stroke={brand.orange}
                strokeWidth={1.2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 12, height: 3, borderRadius: 1, backgroundColor: theme.palette.primary.main }} />
          <Typography variant="caption" color="text.secondary">
            {activeMetric.label}
          </Typography>
        </Stack>
        {showKeyEventsBar && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 8,
                borderRadius: "2px",
                backgroundColor: alpha(theme.palette.primary.main, 0.15),
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Eventos clave
            </Typography>
          </Stack>
        )}
        {compareEnabled && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 12, height: 3, borderRadius: 1, backgroundColor: brand.orange }} />
            <Typography variant="caption" color="text.secondary">
              Periodo anterior
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default TrafficChart;
